import {CaretDownIcon, CaretUpIcon, Icon, Pane} from "evergreen-ui";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "providerapp/pagination";
import React, {Dispatch} from "react";
import {
    CareTeamSortBy,
    CLIENT_PROFILE_CARE_TEAM_MEMBERS_PATH,
    SortOrder,
    TeamContact,
    TeamContactColumn,
    TeamContactRoleMap
} from "../domain";
import {CareTeamListAction} from "./practitionerCareTeamListReducer";
import {LoadingSpinner, Paths, RelayConnection, ErrorDisplay} from "common";
import {gql, useQuery} from "@apollo/client";
import {useLocation} from "react-router";
import {get} from "lodash";
import {formatDateStr} from "../../../common/util/date";
import {fullName} from "../../../core/domain";
import {FatOutlineButton} from "../../../common/component/FatOutlineButton";

const CareTeamListColumns: Readonly<TeamContactColumn[]> = [
    {name: 'Name', attr: 'firstName'},
    {name: 'Role', attr: 'memberRole'},
    {name: 'Start Date', attr: 'startDate'},
]

type CareTeamListProps = {
    clientGuid: string;
    sortBy: CareTeamSortBy;
    pagination: RelayPaginationState;
    dispatch: Dispatch<CareTeamListAction>;
}


export const CARE_TEAM_LIST = gql`
query practitionerCareTeamQuery($first: Int = 20, $after: String, $clientGuid: String, $params: CareTeamListQueryParamsInput!) {
    practitionerCareTeamQuery(
        first: $first
        after: $after
        clientGuid: $clientGuid
        params: $params
    ) {
        edges {
            cursor
            node {
                guid 
                firstName
                lastName
                startDate
                memberRole
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
  } 
`

export type CareTeamQueryResponse = RelayConnection<TeamContact> & { totalElements: number };

export function PractitionerCareTeamList({
                                             clientGuid,
                                             sortBy,
                                             pagination,
                                             dispatch
                                         }: CareTeamListProps) {

    const location = useLocation();

    const {loading, data, error, refetch} = useQuery<{ practitionerCareTeamQuery: CareTeamQueryResponse }, {}>(
        CARE_TEAM_LIST, {
            skip: !location.pathname.includes(CLIENT_PROFILE_CARE_TEAM_MEMBERS_PATH),
            variables: {
                ...pagination.toGraphQlParams(),
                clientGuid: clientGuid,
                params: {
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            },
            onCompleted: () => {
                dispatch({type: 'SET_FETCH_PRACTITIONER_LIST_DONE', refetchFunc: refetch})
            },
        },
    )

    const setSortByColumn = (newSortColumn: Paths<TeamContact>) => {
        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    const displayCol = (contact: TeamContact, attr: Paths<TeamContact>) => {
        const value = get(contact, attr);
        if (attr === "memberRole" && value) {
            return TeamContactRoleMap.get(value);
        } else if (attr === "startDate" && value) {
            return formatDateStr(value);
        } else if (attr === "firstName" && value) {
            return fullName(contact)
        }

        return value;
    }

    let totalCareTeamMembers: number = 0;
    if (data?.practitionerCareTeamQuery?.pageInfo?.endCursor !== null) {
        totalCareTeamMembers = data?.practitionerCareTeamQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalCareTeamMembers);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading care team members" />;
    }

    return (
        <div className="h-full p-5 bg-white shadow-md scrollbar-always">
            <div className="flex flex-col w-full relative">
                <Pane display='flex' justifyContent='space-between' padding={20}>
                    <div className='flex'>
                        <FatOutlineButton color={'atlantis'} text={"Add Care Team"}
                                          onClick={() => dispatch({type: 'SET_SHOW_NEW_MODAL'})}
                        />
                    </div>
                    <RelayPagination pageInfo={data?.practitionerCareTeamQuery.pageInfo} count={totalCareTeamMembers}
                                     onClick={onPaginationClick}/>
                </Pane>
                <table className='table-auto w-full'>
                    <thead>
                    <tr className='text-left select-none text-xs text-gray-portal-table-title'>
                        {
                            CareTeamListColumns.map((column) => (
                                <th
                                    key={column.attr}
                                    className={'font-light' + (column.attr === 'firstName' ? ' pl-4' : '')}
                                    onClick={() => setSortByColumn(column.attr)}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        !loading && data!.practitionerCareTeamQuery.edges.map((edge) => edge.node).map((file) =>
                            <tr key={file.guid}
                                className="border-b text-sm border-gray-200 hover:bg-botticelli-100 hover:text-white">
                                {
                                    CareTeamListColumns.map((column) => (
                                        <td
                                            key={column.name}
                                            className={'py-5' + (column.attr === 'firstName' ? ' pl-4' : '')}
                                        >
                                            {
                                                displayCol(file, column.attr)
                                            }
                                        </td>
                                    ))
                                }
                            </tr>)
                    }
                    </tbody>
                </table>
                {
                    loading && <LoadingSpinner spacing={20} height={"60vh"}/>
                }
                {
                    totalCareTeamMembers === 0 && !loading && <div className="text-center p-8 w-full">
                        No Care Team Member matched the searching criteria.
                    </div>
                }
            </div>
        </div>
    );
}